import { create } from "zustand";

export type PanelId = "home" | "live" | "resources" | "device" | "data" | "mine";
export type Locale = "zh-CN" | "en-US";
export type Theme = "light" | "dark" | "system";

export interface DemoLog {
  id: string;
  at: string;
  level: "info" | "success" | "error";
  action: string;
  detail: string;
}

interface DemoState {
  activePanel: PanelId;
  locale: Locale;
  theme: Theme;
  domainId: string;
  endpointId: string;
  roomId: string;
  logs: DemoLog[];
  setPanel: (activePanel: PanelId) => void;
  setLocale: (locale: Locale) => void;
  setTheme: (theme: Theme) => void;
  setTarget: (key: "domainId" | "endpointId" | "roomId", value: string) => void;
  addLog: (entry: Omit<DemoLog, "id" | "at">) => void;
  clearLogs: () => void;
}

function readPreference<T extends string>(key: string, fallback: T, allowed: readonly T[]): T {
  const value = window.localStorage.getItem(key) as T | null;
  return value && allowed.includes(value) ? value : fallback;
}

export const useDemoStore = create<DemoState>((set) => ({
  activePanel: "home",
  locale: readPreference("hl.demo.locale", "en-US", ["zh-CN", "en-US"]),
  theme: readPreference("hl.demo.theme", "system", ["light", "dark", "system"]),
  domainId: "",
  endpointId: "",
  roomId: "",
  logs: [],
  setPanel: (activePanel) => set({ activePanel }),
  setLocale: (locale) => {
    window.localStorage.setItem("hl.demo.locale", locale);
    set({ locale });
  },
  setTheme: (theme) => {
    window.localStorage.setItem("hl.demo.theme", theme);
    set({ theme });
  },
  setTarget: (key, value) => set({ [key]: value } as Pick<DemoState, typeof key>),
  addLog: (entry) => set((state) => ({ logs: [{ ...entry, id: crypto.randomUUID(), at: new Date().toISOString() }, ...state.logs].slice(0, 80) })),
  clearLogs: () => set({ logs: [] }),
}));
